/**
 * Video Approval Comparison Chart
 * ================================
 * Shows video approval trends: Daily, Weekly, Monthly comparisons
 * Helps supervisors correlate video approvals with sales performance.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { userManagementApi } from '../../api/userManagement';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Chart colors for comparison periods
const PERIOD_COLORS = {
  current: '#0072E5',      // EMZI Blue - Current period
  last_month: '#F7941E',   // EMZI Orange - 1 month ago
  two_months: '#9CA3AF'    // Gray - 2 months ago
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg border border-gray-200 text-xs">
        <p className="font-bold text-gray-800 mb-1">{label}</p>
        <p style={{ color: PERIOD_COLORS.current }}>
          🎬 Video Approved: <span className="font-bold">{payload[0]?.value || 0}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Trend indicator component
const TrendIndicator = ({ trend }) => {
  const isPositive = trend > 0;
  const isZero = trend === 0;
  
  return (
    <span className={`text-xs font-bold flex items-center gap-0.5 ${
      isZero ? 'text-gray-500' : isPositive ? 'text-green-600' : 'text-red-500'
    }`}>
      {isZero ? '→' : isPositive ? '↑' : '↓'}
      {Math.abs(trend)}%
    </span>
  );
};

const VideoApprovalComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('daily'); // 'daily' | 'weekly' | 'monthly'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userManagementApi.getVideoApprovalComparison();
      setData(response);
    } catch (err) {
      console.error('Failed to load video comparison:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Transform data for bar chart based on active view
  const getChartData = () => {
    if (!data) return [];
    
    const periodData = data[activeView];
    if (!periodData) return [];
    
    return [
      {
        name: periodData.current.label,
        value: periodData.current.count,
        date: periodData.current.date,
        color: PERIOD_COLORS.current
      },
      {
        name: periodData.last_month.label,
        value: periodData.last_month.count,
        date: periodData.last_month.date,
        color: PERIOD_COLORS.last_month
      },
      {
        name: periodData.two_months.label,
        value: periodData.two_months.count,
        date: periodData.two_months.date,
        color: PERIOD_COLORS.two_months
      }
    ];
  };

  // Get current period stats
  const getCurrentStats = () => {
    if (!data) return { current: 0, trend: 0 };
    const periodData = data[activeView];
    return {
      current: periodData?.current?.count || 0,
      trend: periodData?.trend || 0
    };
  };

  if (loading) {
    return (
      <Card className="mt-3" style={{ borderColor: EMZI_COLORS.border }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <div 
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${EMZI_COLORS.primary} transparent` }}
            ></div>
            <span className="ml-2 text-sm text-gray-500">Loading comparison...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-3" style={{ borderColor: EMZI_COLORS.error }}>
        <CardContent className="p-4 text-center">
          <p className="text-sm text-red-500 mb-2">{error}</p>
          <Button onClick={loadData} size="sm" style={{ backgroundColor: EMZI_COLORS.primary }}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const chartData = getChartData();
  const stats = getCurrentStats();

  return (
    <Card className="mt-3 overflow-hidden" style={{ borderColor: EMZI_COLORS.border }}>
      {/* Header */}
      <CardHeader className="px-3 py-2 border-b bg-gradient-to-r from-purple-50 to-white">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            🎬 Video Approval Trend
            <TrendIndicator trend={stats.trend} />
          </CardTitle>
          
          {/* Period Toggle */}
          <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5">
            {[
              { id: 'daily', label: 'Hari' },
              { id: 'weekly', label: 'Minggu' },
              { id: 'monthly', label: 'Bulan' }
            ].map(period => (
              <button
                key={period.id}
                onClick={() => setActiveView(period.id)}
                className={`px-2 py-1 text-xs rounded transition-all ${
                  activeView === period.id 
                    ? 'bg-white text-purple-600 shadow-sm font-semibold' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3">
        {/* Summary Stats Row */}
        <div className="flex gap-2 mb-3">
          {chartData.map((item, index) => (
            <div 
              key={`item-${index}`}
              className="flex-1 p-2 rounded-lg text-center"
              style={{ 
                backgroundColor: `${item.color}15`,
                border: `1px solid ${item.color}30`
              }}
            >
              <div 
                className="text-lg font-black"
                style={{ color: item.color }}
              >
                {item.value}
              </div>
              <div className="text-[10px] text-gray-600 font-medium">{item.name}</div>
              <div className="text-[9px] text-gray-400">{item.date}</div>
            </div>
          ))}
        </div>

        {/* Bar Chart */}
        <div className="bg-gray-50 rounded-xl p-3">
          <ResponsiveContainer width="100%" height={140}>
            <BarChart 
              data={chartData} 
              margin={{ top: 10, right: 10, left: -15, bottom: 5 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 10, fill: '#666' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: '#666' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-4 mt-2">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: PERIOD_COLORS.current }}></div>
            <span className="text-[9px] text-gray-500">Semasa</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: PERIOD_COLORS.last_month }}></div>
            <span className="text-[9px] text-gray-500">1 Bulan Lepas</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: PERIOD_COLORS.two_months }}></div>
            <span className="text-[9px] text-gray-500">2 Bulan Lepas</span>
          </div>
        </div>

        {/* Insight Box */}
        <div 
          className="mt-3 p-2 rounded-lg text-xs"
          style={{ 
            backgroundColor: stats.trend >= 0 ? '#ECFDF5' : '#FEF2F2',
            border: `1px solid ${stats.trend >= 0 ? '#A7F3D0' : '#FECACA'}`
          }}
        >
          <span className="font-semibold">
            {stats.trend >= 0 ? '📈' : '📉'} Insight:
          </span>
          {' '}
          {stats.trend > 0 
            ? `Video approvals meningkat ${stats.trend}% berbanding bulan lepas.`
            : stats.trend < 0 
              ? `Video approvals menurun ${Math.abs(stats.trend)}% berbanding bulan lepas.`
              : 'Video approvals sama dengan bulan lepas.'
          }
          <span className="text-gray-500 block mt-1">
            Bandingkan trend ini dengan sales performance untuk analisis yang lebih mendalam.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default VideoApprovalComparison;
